import { z } from 'zod';

import { logger } from '@/config/logger';
import { NotificationId, NotificationStatus, type Notification } from '@/modules/api/codegen';

import { idTokenSchema } from './jwt';

const knownNotificationIds = new Set<string>(Object.values(NotificationId));

export const authUserSchema = z.object({
    id: z.string(),
    domain: z.string(),
    email: z.string().email(),
    isAdmin: z.boolean(),
    name: z.string(),
    photoUrl: z.string().optional(),
    /** For demo */
    idToken: z.string().optional(),
    decodedIdToken: idTokenSchema.optional(),
    /** Available only right after the user signs in. Undefined after refreshing the page. */
    isFirstLoginSaas: z.boolean().optional(),

    notifications: z
        .object({
            id: z.string(),
            status: z.nativeEnum(NotificationStatus),
            dismissedAt: z.string().datetime().optional(),
        })
        .array()
        // A notification id shipped by a newer backend must not fail the whole auth payload.
        .transform(notifications =>
            notifications.filter((notification): notification is Notification => knownNotificationIds.has(notification.id)),
        ),
});

export type AuthUser = z.infer<typeof authUserSchema>;

export const oAuthExtensionResultStateSchema = z.object({
    extensionId: z.string(),
    nonce: z.string(),
});

export type OAuthExtensionResultState = z.infer<typeof oAuthExtensionResultStateSchema>;

const oAuthResultStateSchema = oAuthExtensionResultStateSchema.or(
    z.object({
        url: z.string(),
    }),
);

export type OAuthResultState = z.infer<typeof oAuthResultStateSchema>;

const oAuthStateSchema = z.string().transform(state => {
    try {
        return oAuthResultStateSchema.parse(JSON.parse(decodeURIComponent(state)));
    } catch (error) {
        logger.error(error);
        return null;
    }
});

export const oAuthCodeResultSchema = z
    .object({
        code: z.string(),
        state: oAuthStateSchema,
    })
    .passthrough();

export type OAuthCodeResult = z.infer<typeof oAuthCodeResultSchema>;

export const oAuthIdTokenResultSchema = z
    .object({
        id_token: z.string(),
        state: oAuthStateSchema,
    })
    .passthrough();

export type OAuthIdTokenResult = z.infer<typeof oAuthIdTokenResultSchema>;
